// hooks/useTextChat.ts
//
// State machine for one open text-chat sheet: the current thread, the live
// streamed reply, and continuation handling. The anchor is captured once when
// the sheet opens (per-session, per the brief) and reused for every send.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatAnchor,
  ChatMessage,
  ChatSessionSummary,
} from '@/types/chat';
import {
  sendChatMessage,
  fetchSessionMessages,
  ChatStreamHandle,
} from '@/services/chatApi';
import { useNetworkState } from '@/hooks/useNetworkState';

interface StartParams {
  bookId: string;
  anchor: ChatAnchor;
  excerpt?: string | null;
  maxRead?: string | null;
}

interface MessageLifecycleHandlers {
  onAccepted?: (sessionId: number) => void;
  onRejectedBeforeAcceptance?: () => void;
}

function nowIso(): string {
  return new Date().toISOString();
}

export function useTextChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [isContinuation, setIsContinuation] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);

  // Mirrored into a ref because runStream is a stable callback that must read
  // the latest value without being re-created on every connection change.
  const { isOffline } = useNetworkState();
  const isOfflineRef = useRef(isOffline);
  useEffect(() => {
    isOfflineRef.current = isOffline;
  }, [isOffline]);

  // Send context, captured at open / first load. Kept in refs so the stream
  // handlers always read the latest without re-creating callbacks.
  const bookIdRef = useRef<string>('');
  const anchorRef = useRef<ChatAnchor | null>(null);
  const excerptRef = useRef<string | null>(null);
  const maxReadRef = useRef<string | null>(null);

  const streamingRef = useRef<string>('');
  const lastSentTextRef = useRef<string>('');
  const lastLifecycleHandlersRef = useRef<MessageLifecycleHandlers | undefined>(undefined);
  const handleRef = useRef<ChatStreamHandle | null>(null);
  // Mirror of `sessionId` so `runStream` always sends the current conversation
  // id without being re-created (and going stale) on every state change. Null
  // means "start a new conversation on the next send".
  const sessionIdRef = useRef<number | null>(null);
  const tempIdRef = useRef(-1);

  const applySessionId = useCallback((id: number | null) => {
    sessionIdRef.current = id;
    setSessionId(id);
  }, []);

  const abortActive = useCallback(() => {
    handleRef.current?.abort();
    handleRef.current = null;
  }, []);

  /** Clear everything and stop any in-flight stream. */
  const reset = useCallback(() => {
    abortActive();
    streamingRef.current = '';
    sessionIdRef.current = null;
    setMessages([]);
    setStreamingText('');
    setIsStreaming(false);
    setError(null);
    setSessionId(null);
    setIsContinuation(false);
    setLoadingThread(false);
  }, [abortActive]);

  /** Open a fresh, empty sheet pinned at the given anchor. */
  const start = useCallback(
    ({ bookId, anchor, excerpt, maxRead }: StartParams) => {
      reset();
      bookIdRef.current = bookId;
      anchorRef.current = anchor;
      excerptRef.current = excerpt ?? null;
      maxReadRef.current = maxRead ?? null;
    },
    [reset]
  );

  /** Open an existing conversation from the history list and load its thread. */
  const openSession = useCallback(
    async (
      summary: ChatSessionSummary,
      bookId: string,
      maxRead?: string | null,
      excerpt?: string | null
    ) => {
      reset();
      bookIdRef.current = bookId;
      anchorRef.current = {
        chapter_id: summary.anchor_chapter_id,
        sentence_index: summary.anchor_sentence_index,
        time_offset: summary.anchor_time_offset ?? undefined,
      };
      excerptRef.current = excerpt ?? null;
      maxReadRef.current = maxRead ?? null;
      applySessionId(summary.id);
      setIsContinuation(true);
      setLoadingThread(true);
      try {
        const thread = await fetchSessionMessages(summary.id);
        // Tool calls come back as role 'tool'; nothing renders them yet.
        setMessages(thread.messages.filter((m) => m.role !== 'tool'));
      } catch (e) {
        setError((e as Error)?.message || 'Could not load this conversation.');
      } finally {
        setLoadingThread(false);
      }
    },
    [reset, applySessionId]
  );

  /** Stream a reply for `text`. Does not touch the user-message list. */
  const runStream = useCallback((text: string, lifecycle?: MessageLifecycleHandlers) => {
    const anchor = anchorRef.current;
    if (!anchor || !bookIdRef.current) return;

    // Say so outright rather than opening a stream that cannot connect. The
    // reply needs the server, so there is nothing to degrade to - but a
    // stalled spinner that eventually times out reads as a broken feature.
    if (isOfflineRef.current) {
      setError('You are offline. Reconnect to keep the conversation going.');
      return;
    }

    let accepted = false;

    setError(null);
    setIsStreaming(true);
    streamingRef.current = '';
    setStreamingText('');

    handleRef.current = sendChatMessage(
      {
        book_id: bookIdRef.current,
        anchor,
        message: text,
        // null id → new conversation; the server echoes back the created id,
        // which we capture so the rest of this thread appends to it.
        session_id: sessionIdRef.current ?? undefined,
        current_excerpt: excerptRef.current || undefined,
        max_read_position: maxReadRef.current || undefined,
      },
      {
        onSession: (e) => {
          accepted = true;
          applySessionId(e.session_id);
          lifecycle?.onAccepted?.(e.session_id);
        },
        onDelta: (e) => {
          streamingRef.current += e.text;
          setStreamingText(streamingRef.current);
        },
        onDone: (e) => {
          const content = streamingRef.current;
          if (content) {
            setMessages((prev) => [
              ...prev,
              { id: e.message_id, role: 'assistant', content, created_at: nowIso() },
            ]);
          }
          streamingRef.current = '';
          setStreamingText('');
          setIsStreaming(false);
          handleRef.current = null;
        },
        onError: (e) => {
          if (!accepted) lifecycle?.onRejectedBeforeAcceptance?.();
          // Drop the partial; surface the error with a retry affordance.
          streamingRef.current = '';
          setStreamingText('');
          setIsStreaming(false);
          setError(e.message || 'Something went wrong.');
          handleRef.current = null;
        },
      }
    );
  }, [applySessionId]);

  /** Append the user's message and stream the reply. */
  const sendMessage = useCallback(
    (text: string, lifecycle?: MessageLifecycleHandlers) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;
      const userMsg: ChatMessage = {
        id: tempIdRef.current--,
        role: 'user',
        content: trimmed,
        created_at: nowIso(),
      };
      setMessages((prev) => [...prev, userMsg]);
      lastSentTextRef.current = trimmed;
      lastLifecycleHandlersRef.current = lifecycle;
      runStream(trimmed, lifecycle);
    },
    [isStreaming, runStream]
  );

  /**
   * Retry after a mid-stream failure. Resends the same text *without* adding a
   * second user bubble. Note: the user message was already persisted before the
   * AI call, so the backend may end up with a doubled user turn — we tolerate it.
   */
  const retry = useCallback(() => {
    if (isStreaming || !lastSentTextRef.current) return;
    runStream(lastSentTextRef.current, lastLifecycleHandlersRef.current);
  }, [isStreaming, runStream]);

  return {
    messages,
    streamingText,
    isStreaming,
    error,
    sessionId,
    isContinuation,
    loadingThread,
    start,
    openSession,
    sendMessage,
    retry,
    reset,
  };
}
